
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { Box, Tabs, Tab, styled } from '@mui/material';
import { AppDispatch } from '../../../store';
import UserProfileTab from '../../../components/UserProfileTab';
import PageCard from '../../../components/PageCard';

import {
  fetchWorkerDetail,
  selectWorkerDetails,
  IWorkerUser,
} from '../../../modules/worker';
import { updateWorkerStatus } from '../../../modules/worker/actions';

import ProfileAndDetails from './components/ProfileAndDetails';
import ShiftHistory from './components/ShiftHistory';
import AlertPopup from '../../../components/common/AlertPopup';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`worker-tabpanel-${index}`}
      aria-labelledby={`worker-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: '1px solid #e8e8e8',
  '& .MuiIndicator': {
    backgroundColor: '#4D7C72', // Match the green color from screenshot
    height: 3,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  color: '#666',
  marginRight: theme.spacing(4),
  '&.Mui-selected': {
    color: '#4D7C72',
  },
  '&:hover': {
    color: '#4D7C72',
    opacity: 0.8,
  },
}));

const WorkerDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch: AppDispatch = useDispatch();
  const workerDetails = useSelector(selectWorkerDetails);

  const [value, setValue] = useState(0);
  const [workerId, setWorkerId] = useState<number>(0);
  const [user, setUser] = useState<IWorkerUser>({
    email: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    profile_picture: ""
  });
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    setPath([
      { title: 'Management', path: '/' },
      { title: 'Worker', path: '/workers' },
      { title: 'Details', path: '' }, // Or worker name
    ]);
    setIsShow(true);
  }, [setPath, setIsShow]);

  useEffect(() => {
    if (id) {
      const wId = parseInt(id);
      setWorkerId(wId);
      dispatch(fetchWorkerDetail(wId) as any);
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (workerDetails) {
      setUser(workerDetails.user);
    }
  }, [workerDetails]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleStatusChange = async (status: string, reason?: string) => {
    try {
      await dispatch(updateWorkerStatus({ workerId, action: status, reason }) as any).unwrap();
      setAlertMessage(`Worker status updated to ${status} successfully`);
      setAlertSeverity('success');
      setAlertOpen(true);
      dispatch(fetchWorkerDetail(workerId) as any);
    } catch (error: any) {
      setAlertMessage(error || 'Failed to update worker status');
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  };

  return (
    <Box>
      {workerDetails &&
        <PageCard padding={4} backgroundRequired={true}>
          <UserProfileTab
            user={user}
            id={workerId}
            status={workerDetails.worker.status}
            showActionButton={true}
            onClickButton={handleStatusChange}
          />
        </PageCard>
      }

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <StyledTabs value={value} onChange={handleChange} aria-label="worker details tabs">
            <StyledTab label="Profile & Details" />
            <StyledTab label="Shift History & Compliances" />
          </StyledTabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <ProfileAndDetails workerId={workerId} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <ShiftHistory workerId={workerId} />
        </CustomTabPanel>
      </Box>
      <AlertPopup
        open={alertOpen}
        message={alertMessage}
        severity={alertSeverity}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
};

export default WorkerDetailsPage;
